import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useTickets } from './TicketContext';
import { usePunishment } from './PunishmentContext';
import { usePayments } from './PaymentContext';
import { useUnreadAnnouncements } from '../hooks/useUnreadAnnouncements';
import { supabase } from '../lib/supabase';
import { safeParseArray } from '../utils/storageUtils';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications deve ser usado dentro de um NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user, updateUser } = useAuth();
    const { tickets, canAccessTicket } = useTickets();
    const { punishments } = usePunishment();
    const { unreadCount: announcementsUnreadCount, unreadIds: announcementsUnreadIds, announcements: allAnnouncements } = useUnreadAnnouncements();
    const { payments } = usePayments();
    const { preRegisteredStaff, staffRegistrations } = useAuth();

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastAnnouncementsCount, setLastAnnouncementsCount] = useState(0);

    // Audio for notifications
    const playNotificationSound = useCallback(() => {
        if (user?.notificationSounds === false) return;
        try {
            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
            audio.volume = 0.5;
            audio.play().catch(error => {
                // Autoplay blocked is common and can be ignored
                console.log('Reprodução de áudio bloqueada: aguardando interação do usuário.');
            });
        } catch (error) {
            console.error('Erro ao tocar som de notificação:', error);
        }
    }, []);

    // Load read notifications from User Profile (Firestore Real-time)
    const getReadStatus = useCallback(() => {
        // If user is loaded, trust the DB/AuthContext state which is synced
        if (user && Array.isArray(user.readNotificationIds)) {
            return user.readNotificationIds;
        }
        // Fallback or empty if not loaded yet
        return [];
    }, [user]);

    const markAsRead = async (id) => {
        if (!user || !user.discordId) return;

        const readIds = getReadStatus();
        if (!readIds.includes(id)) {
            try {
                // Optimistic update via AuthContext (updates local state + localStorage + Supabase)
                // This ensures UI updates immediately without waiting for Supabase/Refresh
                await updateUser({
                    readNotificationIds: [...readIds, id]
                });
            } catch (error) {
                console.error("Error marking notification as read:", error);
            }
        }
    };

    const updateNotifications = useCallback(() => {
        if (!user || user.notificationsEnabled === false) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        const readIds = getReadStatus();
        let newNotifications = [];

        // 1. Process Punições (Advertências/Punições Ativas)
        const notificationTypes = ['Amarração', 'Intimação', 'Alinhamento', 'Solicitar Contato'];
        const userPunishments = punishments.filter(p =>
            (p.userId === user.discordId || p.userId === user.id) &&
            !notificationTypes.includes(p.punishmentType)
        );
        userPunishments.forEach(p => {
            const id = `punish_${p.id}`;
            newNotifications.push({
                id,
                type: 'PUNISHMENT',
                title: 'Atenção: Nova Punição',
                message: `Você recebeu uma ${p.punishmentType} no personagem ${p.characterName}.`,
                date: p.appliedAt,
                link: '/characters',
                read: readIds.includes(id)
            });
        });

        // 2. Process Tickets (Respostas/Status Change)
        const userTickets = tickets.filter(t => t.userId === user.id);
        userTickets.forEach(t => {
            // Se o ticket tem resposta da staff não lida
            const staffResponses = (t.responses || []).filter(r => r && r.isStaff);
            if (staffResponses.length > 0) {
                const latestResponse = staffResponses[staffResponses.length - 1];
                const responseId = `ticket_res_${t.id}_${latestResponse.id}`;
                newNotifications.push({
                    id: responseId,
                    type: 'TICKET_REPLY',
                    title: 'Ticket Respondido',
                    message: `Seu ticket "${t.title}" recebeu uma nova resposta da Staff.`,
                    date: latestResponse.timestamp || latestResponse.createdAt,
                    link: `/tickets/${t.id}`,
                    read: readIds.includes(responseId)
                });
            }

            // Se o status mudou para algo que o usuário precise ver (ex: aguardando_usuario)
            if (Array.isArray(t.auxiliaryStatus) && t.auxiliaryStatus.includes('aguardando_usuario')) {
                const statusId = `ticket_status_${t.id}_waiting`;
                newNotifications.push({
                    id: statusId,
                    type: 'TICKET_STATUS',
                    title: 'Ticket em Aberto',
                    message: `O ticket "${t.title}" está aguardando sua resposta.`,
                    date: t.lastUpdate,
                    link: `/tickets/${t.id}`,
                    read: readIds.includes(statusId)
                });
            }
        });

        // 3. Process Announcements (Comunicados)
        // O hook useUnreadAnnouncements já cuida da contagem, mas queremos listar aqui também
        if (announcementsUnreadCount > 0 && user.showAnnouncements !== false) {
            const latestId = announcementsUnreadIds.length > 0 ? Math.max(...announcementsUnreadIds) : 0;
            const announcementId = `announcements_new_${latestId}`;
            newNotifications.push({
                id: announcementId,
                type: 'ANNOUNCEMENT',
                title: 'Novo Comunicado',
                message: `Você possui ${announcementsUnreadCount} comunicado(s) não lido(s).`,
                date: new Date().toISOString(),
                link: '/announcements',
                read: readIds.includes(announcementId)
            });
        }

        // 4. ADMIN: Pending Payments
        if (user.accessLevel === 'admin') {
            const pendingPayments = payments.filter(p => p.status === 'pending');
            if (pendingPayments.length > 0) {
                const payId = `admin_payments_${pendingPayments.length}`;
                newNotifications.push({
                    id: payId,
                    type: 'ADMIN_PAYMENT',
                    title: 'Pagamentos Pendentes',
                    message: `Existem ${pendingPayments.length} solicitações de pagamento aguardando aprovação.`,
                    date: pendingPayments[0]?.createdAt || new Date().toISOString(),
                    link: '/admin/payments',
                    read: readIds.includes(payId)
                });
            }

            // 5. ADMIN: Staff Approvals
            const pendingStaff = Object.keys(staffRegistrations || {}).length;
            if (pendingStaff > 0) {
                const staffId = `admin_staff_${pendingStaff}`;
                newNotifications.push({
                    id: staffId,
                    type: 'ADMIN_STAFF',
                    title: 'Aprovação de Staff',
                    message: `Existem ${pendingStaff} novos cadastros de staff aguardando.`,
                    date: new Date().toISOString(),
                    link: '/admin/providers',
                    read: readIds.includes(staffId)
                });
            }
        }

        // 6. STAFF: New/Open Tickets in Category
        if (user.accessLevel !== 'user' && user.accessLevel !== 'admin') {
            // Filter tickets available to this staff member that are 'aberto' or 'aguardando_sistema'
            // And are NOT assigned to anyone yet
            const openTickets = tickets.filter(t =>
                (t.status === 'aberto' || t.status === 'aguardando_sistema') &&
                !t.assignedTo &&
                (typeof canAccessTicket === 'function' ? canAccessTicket(t) : false)
            );

            if (openTickets.length > 0) {
                const ticketNotifId = `staff_new_tickets_${openTickets.length}`;
                newNotifications.push({
                    id: ticketNotifId,
                    type: 'STAFF_NEW_TICKET',
                    title: 'Novos Tickets',
                    message: `${openTickets.length} tickets disponíveis para atendimento na sua fila.`,
                    date: openTickets[0]?.lastUpdate || new Date().toISOString(),
                    link: '/dashboard/staff', // Or specific ticket list
                    read: readIds.includes(ticketNotifId)
                });
            }
        }

        // 7. STAFF: Fraud Alerts (Anti-Fraud System)
        if (user.accessLevel !== 'user') {
            try {
                // Filter for internal announcements with metadata.isFraudAlert from the hook data
                const fraudAlerts = (allAnnouncements || []).filter(a =>
                    a.type === 'internal' &&
                    a.metadata?.isFraudAlert &&
                    // Only show recent alerts (last 48 hours) to avoid clutter
                    (new Date() - new Date(a.created_at)) < (48 * 60 * 60 * 1000)
                );

                fraudAlerts.forEach(alert => {
                    const alertId = `fraud_${alert.id}`;
                    newNotifications.push({
                        id: alertId,
                        type: 'FRAUD_ALERT',
                        title: alert.title || '🚨 ALERTA DE FRAUDE',
                        message: alert.content || alert.message,
                        date: alert.created_at,
                        link: alert.ticket_id ? `/staff/tickets/${alert.ticket_id}` : '/staff/triagem',
                        read: readIds.includes(alertId),
                        priority: 'urgent'
                    });
                });
            } catch (error) {
                console.error('[Notifications] Error loading fraud alerts:', error);
            }
        }

        // 8. STAFF: Payment Status Updates
        // Notify staff when their payment request is approved or rejected
        const myPayments = payments.filter(p => p.userId === user.discordId && (p.status === 'approved' || p.status === 'rejected'));
        myPayments.forEach(p => {
            const notifId = `payment_update_${p.id}_${p.status}`;
            const isApproved = p.status === 'approved';

            // Format value safely
            const val = parseFloat(p.totalValue);
            const valueFormatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(!isNaN(val) ? val : 0);

            newNotifications.push({
                id: notifId,
                type: 'PAYMENT_UPDATE',
                title: isApproved ? 'Pagamento Aprovado' : 'Pagamento Rejeitado',
                message: isApproved
                    ? `Sua solicitação de pagamento foi aprovada.`
                    : `Sua solicitação de pagamento foi rejeitada. Motivo: ${p.rejectionReason || 'Não informado'}`,
                date: p.processedAt || p.createdAt,
                link: '/staff/service-provision',
                read: readIds.includes(notifId)
            });
        });

        // Sort by date descending
        newNotifications.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Limit to last 20
        newNotifications = newNotifications.slice(0, 20);

        setNotifications(newNotifications);

        // Count unread (not in readIds AND not already read in its own logic like announcements)
        const unread = newNotifications.filter(n => !n.read).length;

        // If unread count increased, play sound
        if (unread > unreadCount) {
            playNotificationSound();
        }

        setUnreadCount(unread);
    }, [user, punishments, tickets, announcementsUnreadCount, allAnnouncements, getReadStatus, playNotificationSound, unreadCount, canAccessTicket]);

    useEffect(() => {
        updateNotifications();
    }, [updateNotifications]);

    const value = {
        notifications,
        unreadCount,
        markAsRead,
        refreshNotifications: updateNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
